import React, { useEffect, useMemo, useState } from "react";
import { Amplify } from "aws-amplify";
import { generateClient } from "aws-amplify/api";
import * as queries from "../graphql/queries"; 
import {
  Authenticator,
  useAuthenticator,
  Button,
  Card,
  Flex,
  Heading,
  View,
  SelectField,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Badge,
  Text,
  SwitchField
} from "@aws-amplify/ui-react";
import { Link } from "react-router-dom";
import { ChevronRight, Activity, Battery, AlertTriangle, WifiOff, HelpCircle, PlusCircle } from "lucide-react";
import { getAssetTypeOptions } from "../utils/schema/enumUtils";
import { useAdminTenant } from "../admin/AdminTenantContext";
import { gql } from "../lib/gqlClient";
import HealthBadge from "../components/common/HealthBadge";
import AdminTenantRequired from "../components/admin/AdminTenantRequired";

const client = generateClient();

// ---- Helpers ----

function buildHierPrefix({ isActiveBool, propertyCode, buildingCode, unitCode }) {
  const parts = [isActiveBool ? "T" : "F"]; // always include T/F head
  
  if (propertyCode) parts.push(propertyCode);
  if (buildingCode) parts.push(buildingCode);
  if (unitCode) parts.push(unitCode);
  
  return parts.join("#");
}

// ---- Main Component ----
export default function DevicesPage() {
  const { authStatus } = useAuthenticator((ctx) => [ctx.authStatus]);
  const { tenantKey, isPlatformAdmin } = useAdminTenant();

  // Filters
  const [propertyId, setPropertyId] = useState("");
  const [buildingId, setBuildingId] = useState("");
  const [unitId, setUnitId] = useState("");
  const [isActive, setIsActive] = useState("ACTIVE"); // default ACTIVE
  const [assetType, setAssetType] = useState("ALL");
  const [health, setHealth] = useState("ALL");

  // Options
  const [properties, setProperties] = useState([]);
  const [buildings, setBuildings] = useState([]);
  const [units, setUnits] = useState([]);

  // Devices
  const [items, setItems] = useState([]);
  const [nextToken, setNextToken] = useState();
  const [pageToken, setPageToken] = useState();
  const [loading, setLoading] = useState(false);

  const [loadingBuildings, setLoadingBuildings] = useState(false);
  const [loadingUnits, setLoadingUnits] = useState(false);
  const buildingDisabled = !propertyId;
  const unitDisabled     = !buildingId;
  
  const [hasLoaded, setHasLoaded] = useState(false);

  const assetTypeOptions = [
    { value: "ALL", label: "All" },
    ...getAssetTypeOptions(),   
  ];

  const healthOptions = [
    { value: "ALL", label: "All" },
    { value: "LEAK", label: "Leak" },
    { value: "OFFLINE", label: "Offline" },
    { value: "TILTED", label: "Check placement" },
    { value: "LOW_BATTERY", label: "Low battery" },
    { value: "OK", label: "OK" },
    { value: "UNKNOWN", label: "Unknown" },
  ];

  // Load properties for tenant (no pagination; generous limit)
  useEffect(() => {
    if (!tenantKey) return;
    (async () => {
      const data = await gql(queries.propertiesByTenantName, { tenantKey, limit: 1000 });
      setProperties(data?.propertiesByTenantName?.items || []);
    })();
  }, [tenantKey]);

  // Load buildings for selected property (no pagination; generous limit)
  useEffect(() => {
    if (!tenantKey || !propertyId) { setBuildings([]); return; }
    (async () => {
      setLoadingBuildings(true);
      try {
        const data = await gql(
          queries.buildingsByTenantPropertyCode, 
          { 
            tenantKey, 
            propertyIdCode: {
              beginsWith: {
                propertyId
              }
            },
            limit: 2000 
          }
        );
        setBuildings(data?.buildingsByTenantPropertyCode?.items || []);
      } finally {
        setLoadingBuildings(false);
      }
    })();
  }, [tenantKey, propertyId]);

  // Load units for selected building (keep simple pagination as these can be numerous)
  useEffect(() => {
    if (!tenantKey || !buildingId) { setUnits([]); return; }
    (async () => {
      setLoadingUnits(true);
      try {
        let nt, acc = [];
        do {
          const data = await gql(
            queries.unitsByTenantBuildingCode, 
            { 
              tenantKey, 
              buildingIdCode: {
                beginsWith: {
                  buildingId
                }
              },
              limit: 500, 
              nextToken: nt 
            }
          );
          acc = acc.concat(data?.unitsByTenantBuildingCode?.items || []);
          nt = data?.unitsByTenantBuildingCode?.nextToken;
        } while (nt);
        setUnits(acc);
      } finally {
        setLoadingUnits(false);
      }
    })();
  }, [tenantKey, buildingId]);

  // Build hierKey prefix for query (always starts with T/F)
  const hierPrefix = useMemo(() => {
    const isActiveBool = isActive === "ACTIVE";

  const propertyCode =
    propertyId ? properties.find((p) => p.id === propertyId)?.code : undefined;

  const buildingCode =
    buildingId ? buildings.find((b) => b.id === buildingId)?.code : undefined;

  const unitCode =
    unitId ? units.find((u) => u.id === unitId)?.code : undefined;

    return buildHierPrefix({
      isActiveBool,
      propertyCode,
      buildingCode,
      unitCode
    });
  }, [isActive, propertyId, buildingId, unitId, properties, buildings, units]);

  // Fetch devices when filters/page changes
  useEffect(() => {
    if (!tenantKey) return;
    (async () => {
      setLoading(true);
      try {
        const commonVars = {
          tenantKey,
          limit: 25,
          nextToken: pageToken,
          sortDirection: "ASC",
        };

        const healthEnabled = health && health !== "ALL";
        const assetEnabled = assetType && assetType !== "ALL";

        // 1) Health + AssetType
        if (healthEnabled && assetEnabled) {
          const prefix = `${health}#${assetType}#${hierPrefix}`;

          const data = await gql(
            queries.devicesByTenantHealthAssetHierKey,
            {
              ...commonVars,
              healthAssetHierKey: { beginsWith: prefix },
            }
          );

          const { items = [], nextToken: nt } =
            data?.devicesByTenantHealthAssetHierKey || {};
          setItems(items);
          setNextToken(nt);

        // 2) Health only
        } else if (healthEnabled) {
          const prefix = `${health}#${hierPrefix}`;

          const data = await gql(
            queries.devicesByTenantHealthHierKey,
            {
              ...commonVars,
              healthHierKey: { beginsWith: prefix },
            }
          );

          const { items = [], nextToken: nt } =
            data?.devicesByTenantHealthHierKey || {};
          setItems(items);
          setNextToken(nt);

        // 3) AssetType only (your existing GSI)
        } else if (assetEnabled) {
          const data = await gql(
            queries.devicesByTenantHierarchyTimeFiltered,
            {
              ...commonVars,
              assetTypeHierKey: {
                beginsWith: {
                  assetType: assetType,
                  hierKey: hierPrefix,
                },
              },
            }
          );

          const { items = [], nextToken: nt } =
            data?.devicesByTenantHierarchyTimeFiltered || {};
          setItems(items);
          setNextToken(nt);

        // 4) Neither (your existing GSI)
        } else {
          const data = await gql(
            queries.devicesByTenantHierarchyTimeAll,
            {
              ...commonVars,
              hierKey: { beginsWith: hierPrefix },
            }
          );

          const { items = [], nextToken: nt } =
            data?.devicesByTenantHierarchyTimeAll || {};
          setItems(items);
          setNextToken(nt);
        }
      } finally {
        setLoading(false);
        setHasLoaded(true);
      }
    })();
  }, [tenantKey, hierPrefix, assetType, health, pageToken]);

  if (authStatus === "configuring") return <Text>Loading…</Text>;

  if (authStatus !== "authenticated") {
    return (
      <Authenticator>
        {/* You can customize header/footers here if you want */}
      </Authenticator>
    );
  }

  const ActiveToggle = () => (
    <SwitchField
      label="Active"
      labelPosition="top"
      size="small"
      isChecked={isActive === "ACTIVE"}
      onChange={(e) => {
        setIsActive(e.target.checked ? "ACTIVE" : "INACTIVE");
        setPageToken(undefined);
      }}
    />
  );

  function EmptyDevicesState() {
    return (
      <Card variation="outlined">
        <Flex direction="column" alignItems="center" gap="0.75rem" padding="2rem">
          <Heading level={5}>No devices yet</Heading>
          <Text variation="tertiary">
            Add your first device to get started.
          </Text>
          <Button
            as={Link}
            to="/devices/add"
            variation="primary"
            size="small"
          >
            <PlusCircle size={16} /> Add Device
          </Button>
        </Flex>
      </Card>
    );
  }

  const noFiltersApplied =
    !propertyId &&
    !buildingId &&
    !unitId &&
    assetType === "ALL" &&
    health === "ALL" &&
    isActive === "ACTIVE";

  if (isPlatformAdmin && !tenantKey) {
    return (
      <AdminTenantRequired
        title="Devices"
        message="Select a tenant in the top bar before viewing anything."
      />
    );
  }
  
  // --- RENDER ---
  return (
    <View as="main" maxWidth="1200px" margin="0 auto" padding="2rem">
      <Flex direction="column" gap="4rem">
        {/* Page header (title + Add Device button) */}
        <Flex
          as="section"
          alignItems="center"
          justifyContent="space-between"
        >
          <Heading level={3} margin="0" fontWeight="600">
            Devices
          </Heading>

          <Button
            as={Link}
            to="/devices/add"
            variation="primary"
            size="small"
            gap="0.5rem"
          >
            <PlusCircle size={16}/> Add Device
          </Button>
        </Flex>
        {/* Filters */}
        <Card variation="elevated">
          <Flex direction={{ base: "column", medium: "column", large: "row" }} 
          justifyContent="space-between" 
          alignItems={{ base: "stretch", medium: "flex-start" }}
          gap={16}>
            <Flex  direction={{ base: "column", medium: "column", large: "column" }} 
            gap={16}
            wrap="wrap"
            width={{ base: "100%", medium: "auto" }}>
              <View width={{ base: "100%", medium: "100%", large: "16rem" }}>
                <SelectField 
                label="Property"
                size="small" 
                value={propertyId} 
                onChange={(e) => { setPropertyId(e.target.value); setBuildingId(""); setUnitId(""); setPageToken(undefined); }}>
                  <option value="">All</option>
                  {properties.map((p) => (<option key={p.id} value={p.id}>{p.name}</option>))}
                </SelectField>
              </View>

              <View width={{ base: "100%", medium: "100%", large: "16rem" }}>
                <SelectField 
                label="Building"
                size="small" 
                value={buildingId} 
                onChange={(e) => { setBuildingId(e.target.value); setUnitId(""); setPageToken(undefined); }}
                isDisabled={buildingDisabled}>
                  {buildingDisabled ? (
                    <option value="">Select a property first</option>
                  ) : loadingBuildings ? (
                    <option value="">Loading buildings…</option>
                  ) : (
                    <>
                    <option value="">All</option>
                    {buildings.map((b) => (<option key={b.id} value={b.id}>{b.name || b.code}</option>))}
                    </>
                  )}
                </SelectField>
              </View>

              <View width={{ base: "100%", medium: "100%", large: "16rem" }}>
                <SelectField 
                label="Unit"
                size="small" 
                value={unitId} 
                onChange={(e) => { setUnitId(e.target.value); setPageToken(undefined); }}
                isDisabled={unitDisabled}>
                  {unitDisabled ? (
                    <option value="">Select a building first</option>
                  ) : loadingUnits ? (
                    <option value="">Loading units…</option>
                  ) : (
                    <>
                    <option value="">All</option>
                    {units.map((u) => (<option key={u.id} value={u.id}>{u.code}</option>))}
                    </>
                  )}
                </SelectField>
              </View>
            </Flex>

            {/* Active / Inactive toggle */}
            <Flex direction={{ base: "column", medium: "row" }}
              gap={16}
              alignItems={{ base: "stretch", medium: "flex-start" }}
              // on mobile, put this under the left group with some spacing
              marginTop={{ base: 8, medium: 0 }}
              // keep it right-aligned on wide screens, but full width on mobile
              alignSelf={{ base: "stretch", medium: "auto" }}
              width={{ base: "100%", medium: "auto" }}>
              <View width={{ base: "100%", medium: "auto" }}>
                <ActiveToggle />
              </View>

              {/* AssetType */}
              <View width={{ base: "100%", medium: "auto" }}>
                <SelectField 
                  label="Asset Type" 
                  size="small" 
                  value={assetType} 
                  onChange={(e) => { 
                    setAssetType(e.target.value || "ALL"); 
                    setPageToken(undefined); 
                  }}
                >
                  {assetTypeOptions.map((opt) => (
                    <option key={opt.value} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </SelectField>
              </View>
              <View width={{ base: "100%", medium: "auto" }}>
                <SelectField
                  label="Health"
                  size="small"
                  value={health}
                  onChange={(e) => {
                    setHealth(e.target.value || "ALL");
                    setPageToken(undefined);
                  }}
                >
                  {healthOptions.map((opt) => (
                    <option key={opt.value} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </SelectField>
              </View>
            </Flex>
          </Flex>
        </Card>

        {/* Desktop/tablet table */}
        <View display={{ base: "none", medium: "block" }}>
          <View style={{ overflowX: "auto" }}>
            <Table highlightOnHover style={{ tableLayout: 'fixed'}}>
              <TableHead>
                <TableRow>
                  <TableCell as="th">Label / ID</TableCell>
                  <TableCell as="th">Property</TableCell>
                  <TableCell as="th">Building</TableCell>
                  <TableCell as="th">Unit</TableCell>
                  <TableCell as="th">Room</TableCell>
                  <TableCell as="th">Asset</TableCell>
                  <TableCell as="th">Health</TableCell>
                  <TableCell as="th">Created</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {loading ? (
                  <TableRow><TableCell colSpan={8}>loading...</TableCell></TableRow>
                ) : hasLoaded && items.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={8}>
                      {noFiltersApplied ? (
                        <EmptyDevicesState />
                      ) : (
                        "No devices match these filters."
                      )}
                    </TableCell>
                  </TableRow>
                ) : (
                  items.map((d) => (
                    <TableRow key={d.id} onClick={() => (window.location.href = `/devices/${d.id}`)} style={{ cursor: "pointer" }}>
                      <TableCell>
                        <Text fontWeight={600}>{d.label || d.id}</Text>
                        {d.label && (
                          <Text variation="tertiary" fontSize="0.75rem">{d.id}</Text>
                        )}
                      </TableCell>
                      <TableCell>{d.propertyCode || "—"}</TableCell>
                      <TableCell>{d.buildingCode || "—"}</TableCell>
                      <TableCell>{d.unitCode || "—"}</TableCell>
                      <TableCell>{d.room || "—"}</TableCell>
                      <TableCell>{d.assetType || "—"}</TableCell>
                      <TableCell><HealthBadge status={d.healthStatus} /></TableCell>
                      <TableCell>{d.createdAt ? new Date(d.createdAt).toLocaleDateString() : "—"}</TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </View>
        </View>

        {/* Mobile cards */}
        <View display={{ base: "block", medium: "none" }}>
          <Flex direction="column" gap={12}>
            {loading ? (
              <Card>Loading…</Card>
            ) : hasLoaded && items.length === 0 ? (
              noFiltersApplied ? <EmptyDevicesState /> : <Card>No devices match these filters.</Card>
            ) : (
              items.map((d) => (
                <Card key={d.id}>
                  <Flex alignItems="center" justifyContent="space-between">
                    <View>
                      <Flex alignItems="center" gap={8}>
                        <Text fontWeight={600}>{d.label || d.id}</Text>
                        <HealthBadge status={d.healthStatus} />
                      </Flex>
                      <Text variation="tertiary" fontSize="0.85rem">{(d.propertyCode || "—") + " › " + (d.buildingCode || "—") + " › " + (d.unitCode || "—")}</Text>
                      <Text variation="tertiary" fontSize="0.75rem">{[d.room, d.assetType].filter(Boolean).join(" • ")}</Text>
                    </View>
                    <Button onClick={() => (window.location.href = `/devices/${d.id}`)}><ChevronRight /></Button>
                  </Flex>
                </Card>
              ))
            )}
          </Flex>
        </View>

        {/* Pagination */}
        <Flex alignItems="center" justifyContent="space-between">
          <Text variation="tertiary" fontSize="0.75rem">Sorted by hierarchy (hierKey ↑). Prefix includes T/F head from Active/Inactive.</Text>
          <Flex gap={8}>
            <Button onClick={() => setPageToken(undefined)} disabled={!pageToken && !nextToken}>First</Button>
            <Button onClick={() => setPageToken(undefined)} disabled={!pageToken}>Prev</Button>
            <Button onClick={() => setPageToken(nextToken)} disabled={!nextToken}>Next</Button>
          </Flex>
        </Flex>
      </Flex>
    </View>
  );
}
